import { useEffect, useState } from 'react';

import {
  CreateDocumentData,
  UpdateDocumentData,
} from '@/shared/components/ui/upload/file-input-form';
import { ProjectDocument } from '@/shared/types/api';

const generateTempId = () => {
  return 'temp_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
};

export interface DocumentState {
  id?: number;
  tempId?: string;
  title: string;
  file?: File;
  status: 'existing' | 'new' | 'updated' | 'removed';
  metadata: {
    project_id: number;
    type?: string | null;
    visibility: string;
    association?: string | null;
    file_path?: string;
    creator_id?: string;
    org_id?: number;
  };
  createData?: CreateDocumentData;
  updateData?: UpdateDocumentData;
}

export interface DocumentManager {
  documents: DocumentState[];
  addDocument: (fileData: CreateDocumentData) => void;
  updateDocument: (updateData: UpdateDocumentData) => void;
  removeDocument: (documentId?: number, tempId?: string) => void;
  getNewDocuments: () => DocumentState[];
  getUpdatedDocuments: () => DocumentState[];
  getRemovedDocuments: () => Array<{ id: number; path: string }>;
  reset: () => void;
}

export const useDocumentManager = (
  editMode: boolean,
  initialDocuments?: ProjectDocument[],
): DocumentManager => {
  const [documents, setDocuments] = useState<DocumentState[]>([]);
  const [removedDocuments, setRemovedDocuments] = useState<
    Array<{ id: number; path: string }>
  >([]);

  useEffect(() => {
    if (initialDocuments) {
      setDocuments(
        initialDocuments.map((doc) => ({
          id: doc.id,
          title: doc.title,
          status: 'existing' as const,
          metadata: {
            project_id: doc.project_id,
            type: doc.type,
            visibility: doc.visibility,
            association: doc.association,
            file_path: doc.file_path,
            creator_id: doc.creator_id,
            org_id: doc.org_id,
          },
        })),
      );
      setRemovedDocuments([]);
    } else {
      // Clear documents when initialDocuments is undefined
      setDocuments([]);
      setRemovedDocuments([]);
    }
  }, [initialDocuments]);

  const addDocument = (fileData: CreateDocumentData) => {
    const newDocument: DocumentState = {
      tempId: generateTempId(),
      title: fileData.fileDetails.title,
      file: fileData.fileDetails.file,
      status: 'new',
      metadata: {
        project_id: fileData.fileDetails.project,
        type: fileData.fileDetails.type,
        visibility: fileData.fileDetails.visibility,
        association: fileData.fileDetails.association,
        creator_id: fileData.fileDetails.owner,
        org_id: fileData.orgId,
      },
      createData: fileData,
    };
    setDocuments((prev) => [...prev, newDocument]);
  };

  const updateDocument = (updateData: UpdateDocumentData) => {
    setDocuments((prev) =>
      prev.map((doc) => {
        const isMatch = doc.id === updateData.updateDto.id;

        if (isMatch) {
          return {
            ...doc,
            title: updateData.updateDto.title || doc.title,
            status: 'updated' as const,
            metadata: {
              ...doc.metadata,
              project_id:
                updateData.updateDto.project_id || doc.metadata.project_id,
              type: updateData.updateDto.type || doc.metadata.type,
              visibility:
                updateData.updateDto.visibility || doc.metadata.visibility,
              association:
                updateData.updateDto.association || doc.metadata.association,
              org_id: updateData.updateDto.org_id || doc.metadata.org_id,
            },
            updateData,
          };
        }
        return doc;
      }),
    );
  };

  const removeDocument = (documentId?: number, tempId?: string) => {
    setDocuments((prev) =>
      prev.filter((doc) => {
        if (documentId && doc.id === documentId) return false;
        if (tempId && doc.tempId === tempId) return false;
        if (!documentId && !tempId) return false;
        return true;
      }),
    );

    if (documentId && editMode) {
      const removedDoc = initialDocuments?.find((doc) => doc.id === documentId);
      if (removedDoc) {
        setRemovedDocuments((prev) => [
          ...prev,
          { id: documentId, path: removedDoc.file_path },
        ]);
      }
    }
  };

  return {
    documents,
    addDocument,
    updateDocument,
    removeDocument,
    getNewDocuments: () => documents.filter((doc) => doc.status === 'new'),
    getUpdatedDocuments: () =>
      documents.filter((doc) => doc.status === 'updated'),
    getRemovedDocuments: () => removedDocuments,
    reset: () => {
      setDocuments([]);
      setRemovedDocuments([]);
    },
  };
};
